import { inject, InjectionToken } from '@angular/core';
import {
  ChatMessage,
  ChatMessageTypes,
  InteractionTypes,
  QuestionAttachmentType,
  SurveyResolverStartPayload,
} from '@hyper-inferno/survey-elements-core-utils';
import { IBotService } from './base-survey-bot.interface';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { withEntities } from '@ngrx/signals/entities';
import { QuestionAnswerObject } from '@hyper-inferno/survey-elements-core';
import { Params } from '@angular/router';
import { RenderStates, SurveyRenderControllerStore } from './render.store';
import { Default_Login_Page } from './default_login_page.constant';
import { firstValueFrom } from 'rxjs';

/**
 * Injection Token to switch between Online (SurveyBot) and Offline (CatiBot)
 */
export const BOT_SERVICE_TOKEN = new InjectionToken<IBotService>(
  'BOT_SERVICE_TOKEN',
);

type APPState = {
  messageHandlers: {
    sendMessageHandler?: (
      message: ChatMessage,
      profileIdentifier: string,
    ) => void;
    startTransactionHandler?: (
      surveyId: number,
      profileIdentifier: string,
    ) => Promise<ChatMessage>;
  };
  messageState: {
    latestChatMessage?: ChatMessage;
  };
  disableValidation: boolean;

  handshakeToken: string | null;
  sessionToken: string | null;
  sessionId: string | null;
  context: string;
  metadata: Record<string, any>;
  completedPlugins: string[];
};

const initialState: APPState = {
  messageHandlers: {},
  messageState: {
    latestChatMessage: undefined,
  },
  disableValidation: false,
  handshakeToken: null as string | null,
  sessionToken: localStorage.getItem('survey_auth_token') || null,
  sessionId: null as string | null,
  context: 'unknown',
  metadata: {},
  completedPlugins: [],
};

export const UnifiedSurveyStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),
  withEntities<ChatMessage>(),
  withMethods(
    (
      store,
      renderController = inject(SurveyRenderControllerStore),
      botService = inject(BOT_SERVICE_TOKEN), // The injected abstraction
    ) => ({
      /**
       * Core Communication Methods
       */
      async sendBotMessage(message: ChatMessage) {
        try {
          const responseChatMessage = await botService.sendAnswerBot(message);
          this.receiveBotMessage(responseChatMessage);
        } catch (error) {
          console.error('Bot Communication Error:', error);
        }
      },

      receiveBotMessage(chatMessage: ChatMessage) {
        patchState(store, { messageState: { latestChatMessage: chatMessage } });

        switch (chatMessage.messageType) {
          case ChatMessageTypes.Page:
            renderController.setupCurrentPage(chatMessage.page);
            break;

          case ChatMessageTypes.EndPage:
            // ... existing logic ...
            if ((window as any).flutter_inappwebview) {
              (window as any).flutter_inappwebview.callHandler(
                'stopPassiveMediaRecording',
              );
            }
            renderController.setRenderState(RenderStates.END, {
              endStatePayload: {
                endMessage:
                  chatMessage.page?.submissionInfo?.submissionPayload
                    ?.ending_text ?? 'END of Survey',
              },
            });
            if (
              chatMessage.page?.submissionInfo.submissionPayload.redirect_url
            ) {
              window.open(
                chatMessage.page?.submissionInfo.submissionPayload.redirect_url,
                '_self',
              );
            }
            break;

          case ChatMessageTypes.BOT_ERROR:
            renderController.setRenderState(RenderStates.END, {
              endStatePayload: {
                endMessage: chatMessage.message ?? 'An Error has occurred',
              },
            });
            break;

          case ChatMessageTypes.StartConsent:
            renderController.setRenderState(RenderStates.LOGIN, {
              startConsentPayload: {
                loginPage: chatMessage.message ?? Default_Login_Page,
              },
            });
            break;

          default:
            renderController.setRenderState(RenderStates.END, {
              endStatePayload: {
                endMessage: chatMessage.message ?? 'Unhandled Operation',
              },
            });
            break;
        }
      },

      setAuthToken(sessionToken: string) {
        patchState(store, {
          sessionToken,
        });
      },
      setSessionId(sessionId: string, context: string) {
        patchState(store, {
          sessionId,
          context,
        });
      },

      async startSurveyTransaction(
        resolverPayload: SurveyResolverStartPayload,
      ) {
        try {
          if (navigator.onLine) {
            const handshakeToken = await botService.handshake(resolverPayload);
            // Add the token to subsequent headers
            this.setAuthToken(handshakeToken);
          }

          if (!store.sessionToken?.()) {
            throw new Error('Survey Start Payload could not be determined');
          }

          const firstChatMessage = await botService.startSurveyTransaction(
            store.sessionToken()!,
          );

          if (firstChatMessage.recipient) {
            const sessionId = firstChatMessage.recipient;
            const surveyId = firstChatMessage.sender ?? 'NOT-DEFINED';

            // 1. Update the Angular State
            this.setSessionId(sessionId, surveyId);
          } else {
            throw new Error('Survey Session not started');
          }
          this.receiveBotMessage(firstChatMessage);
        } catch (error:any) {
          throw new Error(`Failed to Start Survey: ${JSON.stringify(error)} `);
        }
      },

      /**
       * User Interaction Handlers
       */
      onSubmitButtonClick(
        message = 'Answer Submitted',
        messageType = ChatMessageTypes.Answer,
        messageMediaUrl?: string,
        mediaType?: QuestionAttachmentType,
        messageTime?: string,
      ) {
        const registry = renderController.registry();
        const answerObject = Object.entries(registry).reduce(
          (acc, [key, value]) => {
            return {
              ...acc,
              [key]: { ...value.data },
            };
          },
          {},
        );

        // We combine the user answers with the metadata collected from plugins
        const finalPayload = {
          ...answerObject,
          ...store.metadata(), // Inject plugin variables here
        };
        this.sendBotMessage({
          messageType,
          message,
          messageMediaUrl,
          mediaType,
          messageTime,
          interactiveMessage: {
            type: InteractionTypes.SUBMIT,
            submitAnswerPayload: {
              answerObject: finalPayload,
            },
          },
          sender: store.sessionId() ?? '',
        });
      },

      // onSkipButtonClick() {
      //   const question = renderController.currentQuestionState()?.question;
      //   if (!question) throw Error('Question not available');

      //   const isSkippedVariable = Object.keys(question.questionMeta).find(
      //     (key) => question.questionMeta[key]?.purpose === 'isSkipped'
      //   );

      //   if (!isSkippedVariable) throw Error('Question is not skippable');

      //   const skipPayload = renderController.coerecedPayloadBuilders(
      //     { [isSkippedVariable]: true },
      //     question.questionMeta
      //   );

      //   this.sendBotMessage({
      //     messageType: ChatMessageTypes.Answer,
      //     message: 'Skipped',
      //     interactiveMessage: {
      //       type: InteractionTypes.SKIP,
      //       skipQuestionPayload: {
      //         questionId: question.id,
      //         answerObject: skipPayload,
      //       },
      //     },
      //   });
      //   renderController.updateSubmitedAsnwersState(skipPayload);
      // },

      onSwitchToAnyQuestionClick(questionId: number) {
        this.sendBotMessage({
          messageType: ChatMessageTypes.Answer,
          message: 'Question Jump',
          interactiveMessage: {
            type: InteractionTypes.CUSTOM_SWITCH,
            customSwitchQuestionPayload: { questionId },
          },
        });
      },

      onLanguageSwitchClick(language: string) {
        this.sendBotMessage({
          messageType: ChatMessageTypes.Answer,
          message: 'Language changed',
          interactiveMessage: {
            type: InteractionTypes.LANGUAGE,
            languageChangePayload: { language },
          },
        });
      },

      onSwitchQuestionClick(isNext: boolean) {
        this.sendBotMessage({
          messageType: ChatMessageTypes.Answer,
          message: 'Navigate button',
          interactiveMessage: {
            type: InteractionTypes.SWITCH,
            switchQuestionPayload: { isNext },
          },
        });
      },

      onLoginButtonClick(
        answerObject: QuestionAnswerObject,
        surveyId: number,
        params: Params,
      ) {
        // this.startSurveyTransaction(surveyId, params, answerObject);
      },

      setupHandlers(
        sendMessageHandler?: (
          message: ChatMessage,
          profileIdentifier: string,
        ) => void,
        startTransactionHandler?: (
          surveyId: number,
          profileIdentifier: string,
        ) => Promise<ChatMessage>,
      ) {
        patchState(store, {
          messageHandlers: { sendMessageHandler, startTransactionHandler },
        });
      },
      updateMetadata(key: string, value: any) {
        patchState(store, (state) => ({
          metadata: {
            ...state.metadata,
            [key]: value,
          },
        }));
      },
      markPluginAsCompleted(pluginId: string) {
        patchState(store, (state) => ({
          completedPlugins: [...state.completedPlugins, pluginId],
        }));
      },
    }),
  ),
);
